import React, { useState, useContext } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FaShoppingCart, FaChevronDown, FaChevronUp, FaArrowRight } from 'react-icons/fa';
import { useCart } from 'react-use-cart';
import { ProductContext } from '../context/ProductsContext';
import CartPanel from '../components/CartPanel';
import { config } from '../config';

const CategoryProducts = () => {
  const { products, categories } = useContext(ProductContext);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { items, totalItems, addItem, updateItemQuantity, removeItem } = useCart();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});

  const toggleDropdown = (productId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleColorSelect = (productId, colorId) => {
    setSelectedOptions(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        colorId,
        sizeId: null // Reset size when color changes
      }
    }));
  };

  const handleSizeSelect = (productId, sizeId) => {
    setSelectedOptions(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        sizeId
      }
    }));
  };

  const handleAddToCart = (product) => {
    // Validate color selection
    if (product.colors?.length > 0 && !selectedOptions[product.id]?.colorId) {
      alert('Please select a color');
      return;
    }

    // Validate size selection for clothing
    if (product.clothing && product.colors?.find(c => c.id === selectedOptions[product.id]?.colorId)?.sizes?.length > 0 && !selectedOptions[product.id]?.sizeId) {
      alert('Please select a size');
      return;
    }

    // Validate size selection for single size products
    if (product.single_product_sizes?.length > 0 && !selectedOptions[product.id]?.sizeId) {
      alert('Please select a size');
      return;
    }

    // Prepare cart item
    let cartItem = { 
      ...product, 
      product_id: product.id,
      price: product.discount_price || product.price,
      id: product.id.toString() // Base ID
    };

    // Handle color variants
    if (product.colors?.length > 0) {
      const selectedColor = product.colors.find(c => c.id === selectedOptions[product.id]?.colorId);
      const selectedSize = product.clothing ? selectedColor?.sizes?.find(s => s.id === selectedOptions[product.id]?.sizeId) : null;

      cartItem = {
        ...cartItem,
        id: `${product.id}-${selectedColor?.id || '0'}${selectedSize ? `-${selectedSize.id}` : ''}`,
        color: selectedColor?.color || null,
        colorId: selectedColor?.id || null,
        size: selectedSize?.size || null,
        sizeId: selectedSize?.id || null,
        image: selectedColor?.image || product.images[0]?.image
      };
    }

    // Handle single product sizes
    if (product.single_product_sizes?.length > 0) {
      const selectedSize = product.single_product_sizes.find(s => s.id === selectedOptions[product.id]?.sizeId);
      
      cartItem = {
        ...cartItem,
        id: `${product.id}-${selectedSize?.id || '0'}`,
        size: selectedSize?.size || null,
        sizeId: selectedSize?.id || null
      };
    }

    addItem(cartItem);
  };

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
  };

  // Filter categories that should be shown on homepage
  const homepageCategories = categories
    .filter(category => category.show_on_homepage === 1)
    .sort((a, b) => a.sort_order - b.sort_order); // Sort by sort_order

  return (
    <div className="bg-gray-50 min-h-screen relative pb-16">
      <div className="container mx-auto py-8 px-4">
        {homepageCategories.map(category => {
          // Get all products for this category
          const allCategoryProducts = products
          .filter(product =>
            product.categories?.some(c => c.id === category.id)
          )
          .reverse();

          // Get limited products for display
          const displayedProducts = allCategoryProducts.slice(0, category.product_limit);
          
          // Check if there are more products than the limit
          const hasMoreProducts = allCategoryProducts.length > category.product_limit;

          // Only show category if it has products
          if (displayedProducts.length === 0) return null;

          return (
            <div key={category.id} className="mb-12">
              <div className="flex justify-between items-center mb-6 border-b pb-2">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                  {category.name}
                </h2>
                
                {hasMoreProducts && (
                  <a
                    href={`/category/${category.slug || category.id}`}
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    View More
                    <FaArrowRight className="ml-1 text-sm" />
                  </a>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                {displayedProducts.map(product => {
                  const discountPercentage = product.discount_price
                    ? Math.round(((product.price - product.discount_price) / product.price) * 100)
                    : 0;

                  const productOptions = selectedOptions[product.id] || {};
                  const selectedColor = product.colors?.find(c => c.id === productOptions.colorId);
                  const isDropdownOpen = openDropdowns[product.id];
                  const hasColors = product.colors?.length > 0;
                  const hasSizes = product.clothing && selectedColor?.sizes?.length > 0;
                  const hasSingleSizes = product.single_product_sizes?.length > 0;
                  const isSingleSizeProduct = hasSingleSizes && !hasColors && !product.clothing;

                  return (
                    <div key={product.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col h-full relative group">
                      {/* Discount Badge */}
                      {product.discount_price && (
                        <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                          {discountPercentage}% OFF
                        </div>
                      )}

                      {/* Product Image */}
                      <div className="relative aspect-square overflow-hidden">
                        <a href={`/${product.slug}`} className="block h-full w-full">
                          {hasColors ? (
                            <div className="h-full w-full">
                              {product.colors.length === 1 ? (
                                <img
                                  src={`${config.imageUrl}/${product.colors[0].image}`}
                                  alt={product.name}
                                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  loading="lazy"
                                />
                              ) : (
                                <Slider {...sliderSettings}>
                                  {product.colors.map(color => (
                                    <div key={color.id} className="h-full w-full">
                                      <img
                                        src={`${config.imageUrl}/${color.image}`}
                                        alt={`${color.color} ${product.name}`}
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        loading="lazy"
                                      />
                                    </div>
                                  ))}
                                </Slider>
                              )}
                            </div>
                          ) : (
                            <img
                              src={`${config.imageUrl}/${product.images[0]?.image}`}
                              alt={product.name}
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                              loading="lazy"
                            />
                          )}
                        </a>
                      </div>

                      {/* Product Info */}
                      <div className="p-3 flex flex-col flex-grow">
                        <h3 className="font-semibold text-gray-800 mb-1 line-clamp-2 text-center">
                          {product.name}
                        </h3>

                        {/* Price */}
                        <div className="flex justify-center items-center gap-2 mt-2">
                          {product.discount_price ? (
                            <>
                              <span className="text-sm text-gray-400 line-through">৳{product.price}</span>
                              <span className="text-lg font-bold text-green-600">৳{product.discount_price}</span>
                            </>
                          ) : (
                            <span className="text-lg font-bold text-green-600">৳{product.price}</span>
                          )}
                        </div>

                        {/* Size selector for single size products */}
                        {isSingleSizeProduct && (
                          <div className="mt-3">
                            <label className="block text-xs text-gray-500 mb-1">Size:</label>
                            <div className="grid grid-cols-4 gap-2">
                              {product.single_product_sizes.map(size => (
                                <button
                                  key={size.id}
                                  onClick={() => handleSizeSelect(product.id, size.id)}
                                  className={`py-1 text-xs rounded ${
                                    productOptions.sizeId === size.id 
                                      ? 'bg-blue-500 text-white' 
                                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                  } transition-colors`}
                                >
                                  {size.size}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Options dropdown for products with variants */}
                        {(hasColors || hasSizes) > 0 && (
                          <div className="mt-3">
                            <button
                              onClick={() => toggleDropdown(product.id)}
                              className="w-full flex justify-between items-center px-3 py-2 bg-gray-100 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              <span>Select Options</span>
                              {isDropdownOpen ? (
                                <FaChevronUp className="text-gray-500 text-xs" />
                              ) : (
                                <FaChevronDown className="text-gray-500 text-xs" />
                              )}
                            </button>

                            {isDropdownOpen && (
                              <div className="mt-2 space-y-3 animate-fadeIn">
                                {/* Color Selection */}
                                {hasColors && (
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Color:</label>
                                    <div className="flex flex-wrap gap-2">
                                      {product.colors.map(color => (
                                        <button
                                          key={color.id}
                                          onClick={() => handleColorSelect(product.id, color.id)}
                                          className={`w-8 h-8 rounded-full border-2 ${
                                            productOptions.colorId === color.id 
                                              ? 'border-blue-500' 
                                              : 'border-gray-300'
                                          } transition-colors`}
                                          style={{ backgroundColor: color.color.toLowerCase() }}
                                          title={color.color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Size Selection */}
                                {hasSizes && (
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Size:</label>
                                    <div className="grid grid-cols-4 gap-2">
                                      {selectedColor?.sizes?.map(size => (
                                        <button
                                          key={size.id}
                                          onClick={() => handleSizeSelect(product.id, size.id)}
                                          className={`py-1 text-xs rounded ${
                                            productOptions.sizeId === size.id 
                                              ? 'bg-blue-500 text-white' 
                                              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                          } transition-colors`}
                                        >
                                          {size.size}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Add to Cart Button */}
                        <button
                          onClick={() => handleAddToCart(product)}
                          className="mt-3 w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2 rounded-md font-medium text-sm flex items-center justify-center gap-2 hover:from-blue-600 hover:to-blue-700 transition-colors shadow-md hover:shadow-lg"
                        >
                          <FaShoppingCart className="text-sm" />
                          Add to Cart
                        </button>
                        
                        {/* Order Button */}
                        <a
                          href={`/${product.slug}`}
                          className="mt-2 w-full text-white font-bold text-sm px-2 py-2 rounded-lg 
                                    bg-gradient-to-r from-green-500 to-green-700 shadow-lg transform transition-all 
                                    hover:scale-[1.02] hover:shadow-xl flex items-center justify-center space-x-2"
                        >
                          <FaShoppingCart />
                          <span>অর্ডার করুন</span>
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* View More Button for Mobile (shown below the products) */}
              {hasMoreProducts && (
                <div className="mt-6 flex justify-center md:hidden">
                  <a
                    href={`/category/${category.slug || category.id}`}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center"
                  >
                    View All {category.name} Products
                    <FaArrowRight className="ml-2 text-sm" />
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Floating Cart Button */}
      <button
        onClick={() => setIsCartOpen(true)}
        className="fixed right-6 top-1/2 -translate-y-1/2 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-10 flex items-center justify-center"
        aria-label="View cart"
      >
        <FaShoppingCart className="text-xl" />
        {totalItems > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
            {totalItems}
          </span>
        )}
      </button>

      {/* Cart Panel */}
      <CartPanel
        isOpen={isCartOpen}
        toggleCart={() => setIsCartOpen(false)}
        cartItems={items}
        removeFromCart={removeItem}
        updateQuantity={updateItemQuantity}
      />
    </div>
  );
};

export default CategoryProducts;